import React, { useEffect, useState } from "react";
import { platformClient } from "@/lib/musicApi";
import { useTranslation } from "react-i18next";

// Hand-built SVG rank medals. Self-contained (own gradient, rim, ribbon, soft
// shadow) so each reads on dark and light with no halo, scales losslessly at any
// size / DPR, and is themeable. Colours match SubscriberBadge / Avatar. The medal
// is never the only signal: name + description render as text beside it, and each
// SVG carries role="img" + a <title>.
const MEDAL = {
  founding: { a: "#F7E39B", b: "#D4AF37", rim: "#A8801F", glyph: "star" },
  pioneer: { a: "#F1F1F4", b: "#B8BCC4", rim: "#8A8E96", glyph: "star" },
  charter: { a: "#E4B489", b: "#CD7F32", rim: "#9A5A22", glyph: "star" },
  member: { a: "#6FA8FF", b: "#2575FC", rim: "#1550B0", glyph: "dot" },
  don: { a: "#C4B5FD", b: "#7C3AED", rim: "#5B21B6", glyph: "check" },
};

const Medal = ({ tier, label, size = 108 }) => {
  const c = MEDAL[tier] || MEDAL.member;
  const gid = `mg-${tier}`;
  return (
    <svg width={size} height={size * 1.18} viewBox="0 0 100 118" role="img" aria-label={label} data-testid={`medal-${tier}`}>
      <defs>
        <radialGradient id={gid} cx="38%" cy="32%" r="75%">
          <stop offset="0%" stopColor={c.a} />
          <stop offset="60%" stopColor={c.b} />
          <stop offset="100%" stopColor={c.rim} />
        </radialGradient>
        <filter id={`sh-${tier}`} x="-30%" y="-30%" width="160%" height="160%">
          <feDropShadow dx="0" dy="2" stdDeviation="3" floodColor="#000" floodOpacity="0.28" />
        </filter>
      </defs>
      {/* ribbon tails */}
      <path d="M35 60 L28 108 L44 96 L50 108 L50 66 Z" fill={c.b} opacity="0.9" />
      <path d="M65 60 L72 108 L56 96 L50 108 L50 66 Z" fill={c.rim} opacity="0.9" />
      {/* medallion */}
      <g filter={`url(#sh-${tier})`}>
        <circle cx="50" cy="42" r="34" fill={`url(#${gid})`} stroke={c.rim} strokeWidth="2" />
        <circle cx="50" cy="42" r="27" fill="none" stroke="#fff" strokeOpacity="0.4" strokeWidth="1.5" />
      </g>
      {c.glyph === "star" && (
        <path d="M50 24 l5.5 11.5 12.5 1.8 -9 8.9 2.1 12.6 -11.1 -5.9 -11.1 5.9 2.1 -12.6 -9 -8.9 12.5 -1.8 Z" fill="#fff" fillOpacity="0.92" />
      )}
      {c.glyph === "dot" && <circle cx="50" cy="42" r="9" fill="#fff" fillOpacity="0.92" />}
      {c.glyph === "check" && (
        <path d="M40 43 l7 7 14 -15" fill="none" stroke="#fff" strokeWidth="5" strokeLinecap="round" strokeLinejoin="round" />
      )}
    </svg>
  );
};

const SEAT_KEYS = { founding: "founding", pioneer: "pioneer", charter: "charter" };

// Pre-launch strip: the medals only, in descending prestige order, with NO tier
// names, NO descriptions, NO seat counts and NO prices. It shows that ranks exist
// and that the early ones are the premium ones, purely through the visuals, without
// revealing what the tiers are called or what anything costs before launch.
const STRIP_ORDER = ["founding", "pioneer", "charter", "member", "don"];

const RankMedalsStrip = () => {
  const { t } = useTranslation();
  return (
    <section className="py-14 px-4" style={{ background: "var(--bg-primary)" }} data-testid="rank-medals-strip">
      <div className="max-w-3xl mx-auto flex items-end justify-center gap-4 sm:gap-8 flex-wrap">
        {STRIP_ORDER.map((tier) => (
          <Medal key={tier} tier={tier} label="" size={72} />
        ))}
      </div>
      <p className="max-w-xl mx-auto text-center text-sm md:text-base mt-6" data-testid="rank-medals-caption" style={{ color: "var(--text-secondary)" }}>
        {t("launch.ranksCaption")}
      </p>
    </section>
  );
};

const RankMedals = ({ stripped = false }) => {
  const { t, i18n } = useTranslation();
  const [data, setData] = useState(null);

  useEffect(() => {
    if (stripped) return; // pre-launch strip is visual-only, no gated fetch
    platformClient
      .get("/home/rank-medals", { params: { lang: i18n.language || "en" } })
      .then((r) => setData(r.data))
      .catch(() => {});
  }, [i18n.language, stripped]);

  if (stripped) return <RankMedalsStrip />;
  if (!data) return null;
  const seatLine = (key) => {
    const s = data.seats && data.seats[SEAT_KEYS[key]];
    if (!s) return null;
    if (s.remaining <= 0) return t("rank.seatsClosed", { defaultValue: "Closed. All {{total}} taken.", total: s.total });
    return t("rank.seatsLeft", { defaultValue: "{{remaining}} of {{total}} seats left", remaining: s.remaining, total: s.total });
  };

  return (
    <section className="py-20 px-4" style={{ background: "var(--bg-primary)" }} data-testid="rank-medals-section">
      <div className="max-w-4xl mx-auto">
        <h2 className="text-3xl sm:text-4xl font-bold text-center mb-4" style={{ color: "var(--text-primary)" }} data-testid="rank-medals-heading">
          {data.heading}
        </h2>
        {data.subline && (
          <p className="text-sm sm:text-base text-center max-w-2xl mx-auto mb-14 leading-relaxed" style={{ color: "var(--text-secondary)" }} data-testid="rank-medals-subline">
            {data.subline}
          </p>
        )}
        <div className="flex flex-col gap-12 sm:gap-16">
          {data.medals.map((m, i) => (
            <div
              key={m.key}
              className={`flex flex-col items-center text-center sm:text-left gap-5 sm:gap-10 ${i % 2 === 1 ? "sm:flex-row-reverse" : "sm:flex-row"}`}
              data-testid={`rank-medal-row-${m.key}`}
            >
              <div className="flex-shrink-0">
                <Medal tier={m.key} label={m.name} />
              </div>
              <div className="flex-1">
                <div className="flex items-center justify-center sm:justify-start gap-3 mb-2 flex-wrap">
                  <h3 className="text-xl font-bold" style={{ color: "var(--text-primary)" }} data-testid={`rank-medal-name-${m.key}`}>{m.name}</h3>
                  {seatLine(m.key) && (
                    <span className="text-xs font-semibold px-2.5 py-1 rounded-full" style={{ background: "var(--bg-subtle)", color: "var(--text-secondary)" }} data-testid={`rank-medal-seats-${m.key}`}>
                      {seatLine(m.key)}
                    </span>
                  )}
                </div>
                <p className="text-sm sm:text-base leading-relaxed" style={{ color: "var(--text-secondary)" }} data-testid={`rank-medal-desc-${m.key}`}>{m.description}</p>
              </div>
            </div>
          ))}
        </div>
        <div className="mt-16 sm:mt-20 max-w-2xl mx-auto text-center rounded-2xl px-6 py-10" style={{ background: "var(--bg-surface)", border: "1px solid var(--border-color)" }} data-testid="rank-medals-earlybird">
          <h3 className="text-2xl font-bold mb-3" style={{ color: "var(--text-primary)" }} data-testid="rank-medals-eb-headline">{data.early_bird_headline}</h3>
          <p className="text-sm sm:text-base leading-relaxed" style={{ color: "var(--text-secondary)" }} data-testid="rank-medals-eb-body">{data.early_bird_body}</p>
        </div>
      </div>
    </section>
  );
};

export default RankMedals;
